export const content = {
  fr: {
    navigation: [
      { label: "Accompagnement", href: "#services" },
      { label: "Résultats", href: "#resultats" },
      { label: "FAQ", href: "#faq" },
      { label: "Notre histoire", href: "/notre-histoire" },
    ],
    hero: {
      badge: "Coaching IA · Comprendre. Construire. Déployer.",
      headingPre: "Pendant que vous lisez ça, l'IA travaille pour ",
      headingAccent: "quelqu'un d'autre",
      headingPost: ".",
      subtitle: "Coaching 1:1 pour dirigeants, indépendants et salariés. En 4 à 6 semaines, on construit ensemble les systèmes IA que vous utiliserez tous les jours — et vous restez autonome après.",
      cta: "Réserver ma session découverte",
      secondaryCta: "Voir si c'est fait pour vous",
      ctaNote: "30 min · Gratuit · Sans engagement",
      stats: [
        { value: "5 max", label: "Clients en simultané" },
        { value: "100%", label: "Accompagnement à distance" },
        { value: "1:1", label: "Relation directe" },
      ],
      availability: "5 clients en simultané",
      availabilityCta: "Candidatures ouvertes",
      scrollLabel: "Découvrir",
    },
    marquee: {
      tools: ["ChatGPT", "Claude", "Gemini", "Perplexity", "Hugging Face", "Hermes", "n8n", "ElevenLabs", "Cursor", "Higgsfield", "OpenClaw", "Stitch", "Genspark", "Paperclip", "Runway", "Notion", "Windsurf", "Emergent"],
    },
    manifesto: {
      pre: "L'IA générative est le ",
      strike: "futur",
      replace: "présent",
      post: " du travail",
      caption: "",
    },
    reality: {
      narrativePre: "Tout le monde a accès aux mêmes outils.",
      narrativeQuote: "Tout le monde ne sait pas les faire travailler.",
      narrativePost: "L'accès n'est plus l'avantage. L'implémentation, si.",
      callouts: [
        "Vous suivez chaque sortie IA.",
        "Vous bookmarkez chaque thread.",
        "Vous regardez chaque démo.",
      ],
      punchline: "Vous suivez chaque lancement. Chaque thread. Chaque démo.",
      punchlineAccent: "Rien n'a changé.",
      sectionLabel: "La réalité",
      statement: "L'IA générative n'est pas le futur.",
      statementAccent: "C'est le présent.",
      statementSub: "Les CEOs, dirigeants et entrepreneurs qui l'ont compris transforment déjà leur façon de travailler.",
      useCases: [
        { label: "Assistants vocaux", icon: "Mic" },
        { label: "Automatisation", icon: "Workflow" },
        { label: "Assistants IA", icon: "Bot" },
        { label: "Outils internes", icon: "Monitor" },
        { label: "Création de contenu", icon: "PenTool" },
      ],
    },
    quotes: {
      items: [
        { text: "Le logiciel dévore le monde, mais l'IA va dévorer le logiciel.", author: "Jensen Huang", role: "CEO, NVIDIA" },
        { text: "L'IA sera la plus grande force d'émancipation économique jamais vue.", author: "Sam Altman", role: "CEO, OpenAI" },
        { text: "L'IA ne sera pas juste un nouvel outil. Ce sera un changement fondamental dans la façon dont la technologie est construite et utilisée.", author: "Marc Andreessen", role: "Andreessen Horowitz" },
        { text: "À l'avenir, chaque entreprise sera une entreprise d'IA.", author: "Satya Nadella", role: "CEO, Microsoft" },
        { text: "L'IA est le symbiote. Elle vous rend plus fort, meilleur, plus rapide.", author: "Varun Mayya", role: "Fondateur & Entrepreneur" },
        { text: "L'IA pourrait être la technologie la plus transformatrice de l'histoire de l'humanité.", author: "Dario Amodei", role: "CEO, Anthropic" },
      ],
    },
    problem: {
      sectionLabel: "Le vrai problème",
      headingPre: "Vous utilisez l'IA. Vos concurrents, eux, la ",
      headingAccent: "déploient",
      headingPost: ".",
      subtitle: "Il y a une différence entre discuter avec ChatGPT et avoir des systèmes IA qui gèrent votre travail. La plupart des professionnels ne franchissent jamais cette ligne.",
      useCases: [
        { persona: "CEO / Dirigeant", quote: "Mon équipe passe 3h par jour sur des tâches que l'IA pourrait faire en 10 minutes. Et je ne sais pas par où commencer pour changer ça.", icon: "Briefcase" },
        { persona: "Freelance", quote: "J'utilise ChatGPT tous les jours mais j'ai l'impression d'utiliser 5% de son potentiel. Je copie-colle des prompts d'influenceurs.", icon: "User" },
        { persona: "Créateur de produit", quote: "J'ai une idée d'app précise mais aucune compétence technique. Tous les devis dépassent 15K€. Je veux apprendre à la construire moi-même avec l'IA.", icon: "Rocket" },
        { persona: "Salarié / Manager", quote: "Mon entreprise parle d'IA depuis 2 ans mais personne ne l'utilise vraiment. Je veux prendre de l'avance avant que ça devienne obligatoire.", icon: "TrendingUp" },
      ],
      codeNoCode: {
        line1: "Vous savez coder ?",
        line1Accent: " Tant mieux.",
        line2: "Vous ne savez pas coder ?",
        line2Accent: " Aucun souci.",
        description: "Notre approche mêle code et no-code. On ne vous forme pas à un outil, on vous apprend à penser en architecte de solutions et à exécuter concrètement, quel que soit votre niveau technique.",
      },
    },
    proof: {
      beforeAfter: {
        heading: "Avant 100xWorks vs Après 100xWorks",
        before: {
          label: "Sans 100xWorks",
          gridLabel: "Vos tâches automatisées",
          gridCaption: "3 tâches automatisées / mois",
          metrics: [
            { title: "15h/semaine de tâches répétitives", sub: "Copier-coller, tri, rédaction manuelle..." },
            { title: "0 système IA en place", sub: "Utilisation ponctuelle de ChatGPT" },
            { title: "Des outils essayés puis abandonnés", sub: "Chaque tâche recommence de zéro" },
          ],
        },
        after: {
          label: "Avec 100xWorks",
          gridLabel: "Vos systèmes IA actifs",
          gridCaption: "17+ systèmes actifs / mois",
          metrics: [
            { title: "3h économisées / jour", sub: "Vos systèmes IA travaillent pendant que vous décidez" },
            { title: "5+ systèmes IA personnalisés", sub: "Rédaction, veille, support, analyse..." },
            { title: "Workflows autonomes", sub: "Tout tourne, même quand vous dormez" },
          ],
        },
        bottomLine: {
          pre: "Récupérez ",
          accent: "15h/semaine",
          post: " de votre temps",
        },
      },
      features: {
        heading: "Ce que vous obtenez",
        subtitle: "Des systèmes concrets, construits avec vous, prêts à tourner.",
        items: [
          { title: "Workflows IA sur mesure", description: "Des chaînes automatisées adaptées à votre métier. De l'entrée à l'action, sans intervention." },
          { title: "Assistants IA personnalisés", description: "Vos propres assistants IA, formés sur votre contexte. Ils travaillent pour vous, pas l'inverse." },
          { title: "Templates & Prompts", description: "Une bibliothèque de prompts optimisés et réutilisables pour chaque situation." },
          { title: "Planning automatisé", description: "Tout est planifié, organisé, prêt à partir. Vous validez, l'IA exécute." },
        ],
      },
    },
    caseStudies: {
      sectionLabel: "Cas concrets",
      headingPre: "Ce que nos clients ",
      headingAccent: "accomplissent",
      headingPost: "",
      subtitle: "Des résultats réels, obtenus par de vrais professionnels pendant leur accompagnement.",
      beforeLabel: "Avant",
      afterLabel: "Après accompagnement",
      cases: [
        {
          persona: "Consultant indépendant",
          track: "AI Mastery One",
          metric: "3h récupérées par jour",
          before: "Passe 4h/jour à rédiger des emails, propositions commerciales et comptes-rendus clients. Chaque document est écrit de zéro.",
          after: "A appris à construire un système qui génère des drafts personnalisés de propositions et d'emails à partir de templates intelligents. Temps de rédaction réduit à 45 min/jour.",
        },
        {
          persona: "PME conseil (8 personnes)",
          track: "AI Mastery Enterprise",
          metric: "Toute l'équipe autonome sur l'IA",
          before: "L'équipe parlait d'IA depuis un an mais personne ne l'utilisait vraiment. Chaque collaborateur dans son coin, sans process commun.",
          after: "A monté en compétences collectivement : chaque membre a co-construit ses propres workflows IA pendant l'accompagnement. Un langage commun, des process partagés, une autonomie réelle.",
        },
        {
          persona: "Entrepreneur en reconversion",
          track: "AI Mastery Build",
          metric: "MVP lancé pour moins de 500 euros",
          before: "A une idée d'application mais aucune compétence technique. Devis reçus : 15 000 à 40 000 euros. Projet bloqué depuis 6 mois.",
          after: "A appris à utiliser des outils no-code et IA pour concevoir, construire et lancer son MVP en 3 semaines, sans écrire une seule ligne de code.",
        },
      ],
    },
    savings: {
      sectionLabel: "Le vrai ROI de l'IA",
      headingPre: "L'IA peut prendre en charge ",
      headingAccent: "ces rôles",
      headingPost: " dans votre entreprise",
      subtitle: "Après notre accompagnement, voici les fonctions que vos systèmes IA peuvent gérer, partiellement ou totalement.",
      totalLabel: "Total économisé :",
      totalSuffix: "/mois",
      note: "+ les outils et abonnements IA nécessaires",
      savingsPrefix: "Économisez",
      savingsUnit: "€/mois",
      roles: [
        { name: "Assistant administratif", saving: 2000 },
        { name: "Community Manager", saving: 2500 },
        { name: "Rédacteur de contenu", saving: 2000 },
        { name: "Service client (N1)", saving: 1800 },
        { name: "Analyste de données", saving: 2500 },
        { name: "Email Marketing", saving: 1500 },
        { name: "Veille concurrentielle", saving: 1200 },
        { name: "Rédacteur SEO", saving: 1800 },
        { name: "Traducteur", saving: 2000 },
        { name: "Prospection commerciale", saving: 2200 },
        { name: "Comptabilité récurrente", saving: 1500 },
        { name: "Social Media Manager", saving: 2500 },
      ],
    },
    readyToBuild: {
      sectionLabel: "Prêt à passer à l'action ?",
      noiseTitle: "Le bruit ne s'arrêtera pas.",
      noiseLines: [
        "Vous avez essayé les prompts pré-faits.",
        "Les formations YouTube. Les threads Twitter.",
        "Les newsletters '10 outils IA de la semaine'.",
        "Et pourtant, rien n'a vraiment changé dans votre quotidien.",
      ],
      whyTitle: "Pourquoi les recettes toutes faites ne marchent pas",
      whyText: "Parce qu'elles ne connaissent pas votre métier, vos contraintes, vos objectifs. Elles sont génériques par nature. Ce qu'il vous faut, c'est du sur mesure.",
      howTitle: "Notre accompagnement en 4 phases",
      phases: [
        { number: "01", title: "Diagnostic", description: "On analyse votre usage actuel, vos tâches quotidiennes et vos points de friction.", duration: "Semaine 1" },
        { number: "02", title: "Fondations", description: "Vous apprenez les principes essentiels : prompting avancé, workflows, outils IA. Adapté à votre niveau.", duration: "Semaines 2-3" },
        { number: "03", title: "Construction", description: "Vous construisez vos propres systèmes : workflows, outils et solutions IA personnalisés pour votre activité.", duration: "Semaines 3-4" },
        { number: "04", title: "Autonomie", description: "Vous êtes autonome. On valide, on ajuste, et vous repartez avec des compétences durables.", duration: "En continu" },
      ],
      cta: "Réserver ma session découverte",
      counter: "Plus que 3 places disponibles",
      metrics: [
        { value: "3h", label: "récupérées par jour en moyenne" },
        { value: "x5", label: "productivité contenu" },
        { value: "<500€", label: "pour lancer un MVP" },
        { value: "100%", label: "des clients autonomes à la fin" },
      ],
    },
    about: {
      sectionLabel: "Nos activités",
      headingPre: "Un fondateur, une ",
      headingAccent: "mission",
      headingPost: ".",
      description: "100xWorks accompagne les entrepreneurs, indépendants et PME dans l'intégration concrète de l'IA au quotidien. Du diagnostic à l'autonomie, on vous aide à construire des systèmes IA qui travaillent pour vous.",
      secondParagraph: "Un seul fondateur, en veille quotidienne sur les dernières avancées IA qu'il teste et traduit en solutions actionnables. Relation directe, du premier échange au résultat. Nombre de clients volontairement limité.",
      stats: [
        { value: "5 max", label: "En simultané" },
        { value: "100%", label: "À distance" },
      ],
      learnMore: "En savoir plus",
      founderQuote: "Un solofounder : votre interlocuteur unique, du début à la fin",
    },
    team: {
      sectionLabel: "L'équipe",
      headingPre: "Les visages derrière ",
      headingAccent: "100xWorks",
      headingPost: "",
      subtitle: "Un fondateur. Trois agents IA. Une équipe hybride qui pratique ce qu'elle enseigne.",
      members: [
        {
          name: "Patrick 'pZ'",
          title: "Founder",
          badge: "Humain",
          bio: "Passionné d'IA et d'entrepreneuriat, Patrick (alias pZ) conçoit les programmes, accompagne les clients et pilote la vision de l'entreprise.",
          image: "https://customer-assets.emergentagent.com/job_541eaea2-9547-41a3-97f7-7400e537e73b/artifacts/otzlsqh5_28140BCA-A077-4FB3-9487-83C62A84B938.PNG",
        },
        {
          name: "Hector",
          title: "Chef AI Officer",
          badge: "Agent IA",
          bio: "Mon bras droit. Hector coordonne les opérations, analyse les données et optimise chaque processus au quotidien.",
          image: "https://static.prod-images.emergentagent.com/jobs/b7ea81d9-46ff-4176-9d90-88843ace2527/images/88ab2d4f62f0328d5a89909870f178b500468c37e41588782e4669f1ae4c0fb9.png",
        },
        {
          name: "Gérard",
          title: "Program Builder",
          badge: "Agent IA",
          bio: "Ancien prof dans l'âme, Gérard m'aide à construire et structurer chaque programme d'accompagnement, avec rigueur et passion.",
          image: "https://static.prod-images.emergentagent.com/jobs/b7ea81d9-46ff-4176-9d90-88843ace2527/images/51028b1c7c53f9792635966621f6a93563fb899486eef62d4bc0d3aeb94829fb.png",
        },
        {
          name: "Laure",
          title: "Support",
          badge: "Agent IA",
          bio: "Laure m'aide à assurer un support réactif et personnalisé pour chaque client, à chaque étape de l'accompagnement.",
          image: "https://static.prod-images.emergentagent.com/jobs/b7ea81d9-46ff-4176-9d90-88843ace2527/images/de0093c5a2409728c24ddeb008c5e2c7725ac0952ae09b715122e791f02e199d.png",
        },
      ],
    },
    amplify: {
      sectionLabel: "Notre philosophie",
      headingPre: "L'IA ne remplace pas les ",
      headingAccent: "humains",
      headingPost: "",
      subtitle: "Elle remplace les tâches. Et elle amplifie ceux qui savent l'utiliser.",
      without: {
        title: "Humain seul",
        subtitle: "Capacité limitée par le temps",
        points: [
          "Tout fait manuellement, tâche par tâche",
          "60% du temps sur du répétitif",
          "Pense ET exécute en parallèle",
          "Plafonne malgré les compétences",
        ],
      },
      with: {
        title: "Humain + IA",
        subtitle: "Capacité multipliée par 10",
        points: [
          "Délègue le répétitif à l'IA",
          "Focus sur la stratégie et les décisions",
          "Pense différemment, exécute différemment",
          "Devient le cerveau, l'IA les bras",
        ],
      },
      pyramid: {
        intro: "Ce qui change avec 100xWorks :",
        layers: [
          { label: "Vision & Stratégie", desc: "Ce que vous faites, et que l'IA ne peut pas remplacer" },
          { label: "Exécution augmentée", desc: "Ce que l'IA amplifie : vous décidez, elle exécute" },
          { label: "Tâches répétitives", desc: "Ce que l'IA remplace : emails, tri, rédaction, veille..." },
        ],
        labelTop: "Vous",
        labelBottom: "L'IA",
      },
      punchline: {
        pre: "On ne vous apprend pas à utiliser un outil. On vous apprend à ",
        accent: "penser et exécuter différemment",
        post: ".",
      },
    },
    services: {
      sectionLabel: "Accompagnement",
      headingPre: "Deux parcours, un seul ",
      headingAccent: "objectif",
      headingPost: " : votre autonomie",
      contact: "Nous contacter",
      learnMore: "En savoir plus",
      items: [
        {
          title: "AI Mastery Build",
          description: "Pour celles et ceux qui veulent construire leur produit avec l'IA générative. De l'idée au prototype fonctionnel : app, interface, outil interne. Vous construisez, on guide. Coaching projet intensif.",
          icon: "Cpu",
        },
        {
          title: "AI Mastery One",
          description: "Pour ceux qui veulent maîtriser l'IA générative au quotidien. De la prise en main des outils à la construction de vos premiers systèmes IA. Sessions 1:1, adaptées à votre niveau.",
          icon: "GraduationCap",
        },
        {
          title: "AI Mastery Enterprise",
          description: "Accompagnement collectif pour les équipes qui veulent monter en compétences sur l'IA. On ne déploie pas pour vous, on co-construit avec vos collaborateurs. Ils apprennent à concevoir et exécuter leurs propres workflows IA, et restent autonomes après.",
          icon: "Search",
        },
      ],
    },
    fit: {
      sectionLabel: "Soyons clairs",
      headingPre: "Cet accompagnement n'est ",
      headingAccent: "pas pour tout le monde",
      headingPost: ".",
    },
    forYou: {
      sectionLabel: "Vous reconnaissez-vous ?",
      headingPre: "Cet accompagnement est ",
      headingAccent: "fait pour vous",
      headingPost: " si...",
      items: [
        "Vous voulez vraiment maîtriser l'IA, pas juste suivre des tutos sur YouTube.",
        "Vous êtes prêt à pratiquer entre les sessions, pas juste à écouter.",
        "Vous cherchez un accompagnement sur mesure adapté à votre métier et votre niveau.",
        "Vous voulez une relation directe avec un interlocuteur unique, pas une plateforme impersonnelle.",
        "Vous voulez des résultats visibles dès les premières semaines, pas dans six mois.",
      ],
    },
    pricing: {
      sectionLabel: "Tarifs",
      headingPre: "Investissement ",
      headingAccent: "transparent",
      headingPost: "",
      subtitle: "Chaque parcours est adapté à vos besoins. Les candidatures sont examinées — la session découverte détermine si on est le bon fit.",
      cta: "Réserver ma session découverte",
      ctaNote: "30 min · Gratuit · Entretien de présélection",
      pausedCta: "Bientôt disponible",
      note: "Les 5 prochaines places sont disponibles à ces tarifs. Le prix augmentera ensuite.",
      tiers: [
        {
          name: "AI Mastery Build",
          tagline: "Construisez votre produit",
          price: "2 500",
          priceRaw: 2500,
          priceUnit: "€",
          priceSuffix: "HT",
          pricePrefix: "",
          duration: "6 semaines",
          features: [
            "Cadrage du projet et faisabilité",
            "Choix des outils et technologies IA",
            "Sessions de coaching intensif",
            "De l'idée au prototype fonctionnel",
            "Itérations et ajustements continus",
            "Accès prioritaire au fondateur",
          ],
          highlighted: false,
          badge: "Founder Mode",
        },
        {
          name: "AI Mastery One",
          tagline: "Maîtrisez l'IA au quotidien",
          price: "1 500",
          priceRaw: 1500,
          priceUnit: "€",
          priceSuffix: "HT",
          pricePrefix: "",
          duration: "4 à 6 semaines",
          features: [
            "Diagnostic de votre usage actuel",
            "Sessions individuelles (1:1)",
            "Prompting, workflows, outils IA",
            "Support entre les sessions",
            "Ressources personnalisées",
          ],
          highlighted: true,
          badge: "Recommandé",
        },
        {
          name: "AI Mastery Enterprise",
          tagline: "Intégrez l'IA dans vos process",
          price: "Sur devis",
          priceRaw: null,
          priceUnit: "",
          priceSuffix: "",
          pricePrefix: "",
          duration: "Sur mesure",
          paused: true,
          badge: "En pause",
          features: [
            "Audit de l'existant",
            "Plan d'action collectif sur mesure",
            "Sessions collectives + 1:1 ciblés",
            "Co-construction de workflows IA avec vos équipes",
            "Suivi et ajustements",
            "Interlocuteur unique : le fondateur",
            "Vos équipes autonomes à la fin",
          ],
          highlighted: false,
        },
      ],
    },
    howItWorks: {
      sectionLabel: "Comment ça marche",
      headingPre: "Un accompagnement, ",
      headingAccent: "pas une formation",
      headingPost: "",
      subtitle: "Pas de programme standardisé, pas de slides génériques : on apprend à se connaître, puis on travaille intensément sur vos cas réels pour un résultat concret en 4 à 6 semaines.",
      steps: [
        {
          number: "01",
          title: "Diagnostic",
          description: "La session découverte de 30 minutes n'était qu'un pré-diagnostic. Ici, on va beaucoup plus loin : votre métier, votre niveau IA réel, vos objectifs précis. Je comprends votre contexte en profondeur avant de construire le plan.",
          detail: "Diagnostic approfondi · Objectifs · Plan d'action",
        },
        {
          number: "02",
          title: "Montée en compétences",
          description: "On construit les fondations ensemble en live : prompting, raisonnement IA, outils adaptés à votre métier. Entre les sessions, vous pratiquez sur des exercices ciblés. Chaque apprentissage s'ancre dans votre réalité — pas de théorie en l'air.",
          detail: "Sessions live · Exercices ciblés · 4 à 6 semaines",
        },
        {
          number: "03",
          title: "Construction",
          description: "On bascule sur votre cas réel : vos tâches, vos documents, vos contraintes. On construit vos systèmes IA, on débloque les points de friction, on itère jusqu'à ce que ça tourne. Support direct entre les séances pour ne pas rester bloqué.",
          detail: "Cas réel · Systèmes IA · Support continu",
        },
      ],
    },
    notForYou: {
      sectionLabel: "Soyons clairs",
      headingPre: "Cet accompagnement n'est ",
      headingAccent: "pas fait pour vous",
      headingPost: " si...",
      items: [
        "Vous cherchez un raccourci pour ne rien apprendre et juste obtenir un résultat clé-en-main.",
        "Vous voulez qu'on fasse à votre place au lieu de monter en compétences vous-même.",
        "Vous n'êtes pas prêt à y consacrer du temps (quelques heures par semaine, régulièrement).",
        "Vous attendez le prompt magique qui résoudra tout en un copier-coller.",
        "Vous cherchez un cours en ligne générique à consommer en autonomie sans interaction.",
      ],
      closing: "Si vous êtes encore là, postulez. La session découverte est un entretien de présélection — l'accompagnement n'est pas automatiquement accordé.",
    },
    testimonials: {
      sectionLabel: "Témoignages",
      headingPre: "Ce qu'en disent ceux qui ont ",
      headingAccent: "franchi le pas",
      headingPost: "",
      items: [
        {
          name: "Marie D.",
          role: "Freelance en marketing digital",
          quote: "Je pensais bien utiliser ChatGPT. En réalité, j'effleurais à peine ce que l'IA pouvait faire pour moi. Après l'accompagnement, j'ai des systèmes IA qui gèrent ma veille, mes emails et ma création de contenu. Un game changer.",
          metric: { value: "3x", label: "Productivité" },
          initials: "MD",
        },
        {
          name: "Thomas L.",
          role: "CEO, startup SaaS (12 pers.)",
          quote: "L'approche est sincère et sans bullshit. Pas de grands discours sur l'IA du futur, juste des solutions concrètes qui marchent aujourd'hui. Et le fait de travailler directement avec le fondateur, ça change tout.",
          metric: { value: "-50%", label: "Tâches manuelles" },
          initials: "TL",
        },
        {
          name: "Sophie B.",
          role: "Dirigeante TPE, conseil RH",
          quote: "J'avais peur de l'IA, je ne savais pas par où commencer. L'accompagnement m'a permis de démystifier tout ça et de mettre en place des systèmes simples mais incroyablement efficaces.",
          metric: { value: "10h", label: "Gagnées par semaine" },
          initials: "SB",
        },
        {
          name: "Pierre M.",
          role: "Indépendant, consultant finance",
          quote: "Ce qui m'a convaincu, c'est l'honnêteté. Pas de promesses de révolution, juste un accompagnement pragmatique qui m'a permis de passer de 'je pose des questions à ChatGPT' à 'l'IA travaille pour moi'.",
          metric: { value: "100%", label: "À distance" },
          initials: "PM",
        },
      ],
    },
    faq: {
      headingPre: "Questions ",
      headingAccent: "fréquentes",
      headingPost: "",
      subtitle: "Tout ce que vous devez savoir avant de vous lancer avec l'IA.",
      items: [
        {
          question: "C'est quoi exactement l'IA générative ?",
          answer: "L'IA générative, c'est une technologie capable de créer du contenu (texte, images, code, audio...) et d'exécuter des tâches complexes pour vous. Là où un moteur de recherche vous donne des liens, l'IA générative rédige vos emails, analyse vos données, crée vos visuels, et automatise vos processus. C'est la différence entre chercher l'information et la faire travailler pour vous.",
        },
        {
          question: "C'est une formation ?",
          answer: "Non, et c'est un choix assumé. 100xWorks n'est pas un organisme de formation : pas de programme standardisé, pas de slides génériques, pas de certificat. C'est un accompagnement : on travaille sur vos cas réels, avec vos outils, jusqu'à ce que vous soyez autonome. Une formation vous transmet un contenu. Un accompagnement vous fait franchir un cap.",
        },
        {
          question: "Comment fonctionne concrètement l'accompagnement ?",
          answer: "Tout commence par un entretien de présélection de 30 minutes. Ce n'est pas un rendez-vous commercial : on évalue ensemble si l'accompagnement est le bon fit pour votre situation. Si c'est le cas, on démarre par un diagnostic approfondi qui pose le plan d'action. Ensuite : montée en compétences sur vos cas réels, construction de vos systèmes IA, support continu. L'objectif : que vous soyez autonome à la fin.",
        },
        {
          question: "Qui réalise l'accompagnement ?",
          answer: "Moi, le fondateur. 100xWorks est une structure à taille humaine. Pas de sous-traitant, pas d'équipe qui change en cours de route. Vous travaillez directement avec la même personne du début à la fin. C'est aussi pour ça que le nombre de clients est limité.",
        },
        {
          question: "Pourquoi si peu de clients en simultané ?",
          answer: "C'est un choix assumé. Travailler avec plus de 5 clients en même temps, c'est diluer l'attention. Chaque accompagnement demande du temps, de l'implication et de l'énergie. Le nombre reste limité pour que chaque client reçoive un engagement total.",
        },
        {
          question: "Faut-il des connaissances techniques ?",
          answer: "Pas du tout. Mon approche est pensée pour être accessible à tous, du débutant complet au profil plus technique. J'adapte le vocabulaire, les exemples et le rythme à votre niveau. L'objectif, c'est de vous rendre autonome, pas de vous impressionner avec du jargon.",
        },
        {
          question: "Quels outils IA utilisez-vous ?",
          answer: "ChatGPT, Claude, Gemini, Perplexity, Hermes, OpenCode, n8n, et d'autres selon les besoins. Je ne suis affilié à aucun outil. Je recommande ceux qui font sens pour votre situation. Le but n'est pas de vous vendre un stack, mais de trouver ce qui marche pour vous.",
        },
        {
          question: "C'est quoi l'autre activité de 100xWorks ?",
          answer: "En parallèle de l'accompagnement IA, 100xWorks conçoit ses propres produits numériques. C'est cette double casquette (builder + accompagnateur) qui nourrit l'approche concrète et pragmatique de l'accompagnement, parce qu'on pratique au quotidien ce qu'on enseigne.",
        },
        {
          question: "Vous travaillez avec les grands groupes ?",
          answer: "C'est possible, mais sous conditions. L'accompagnement est dimensionné pour les particuliers, indépendants, TPE et PME. Pour les grandes structures, il faut que le projet soit compatible avec mon mode de fonctionnement (taille humaine, relation directe, pas de couches hiérarchiques). On en discute lors de l'appel découverte.",
        },
        {
          question: "Tout se fait à distance ?",
          answer: "Oui, 100%. Tous les accompagnements se font en visioconférence. C'est un choix délibéré : plus de flexibilité dans les plannings, pas de déplacements inutiles, et une qualité d'échange qui n'a rien à envier au présentiel.",
        },
        {
          question: "En combien de temps voit-on des résultats ?",
          answer: "Souvent dès les premières sessions. L'idée est de générer des quick wins rapidement : des systèmes simples qui vous font gagner du temps concrètement. Pour aller plus loin (systèmes IA avancés, workflows complexes), comptez 1 à 3 mois selon l'ambition du projet.",
        },
        {
          question: "Peut-on commencer par une mission ponctuelle ?",
          answer: "Absolument. Un audit rapide, une formation d'une journée, un coup de main sur un workflow précis : je m'adapte. Aucune obligation d'engagement long terme. Beaucoup de clients commencent par une mission courte puis reviennent pour aller plus loin.",
        },
      ],
    },
    outcomes: {
      sectionLabel: "Après l'accompagnement",
      headingPre: "Ce que vous ",
      headingAccent: "devenez",
      headingPost: "",
      subtitle: "Chaque parcours mène à un résultat concret. Voici ce qui change pour vous après l'accompagnement.",
      tracks: [
        {
          track: "AI Mastery One",
          profile: "Le professionnel augmenté",
          description: "Vous ne \"testez\" plus l'IA. Vous l'utilisez comme un levier quotidien.",
          outcomes: [
            "Vous gagnez plusieurs heures par semaine grâce à vos systèmes IA",
            "Vous construisez vos propres workflows sans dépendre de personne",
            "Vous savez évaluer et adopter les nouveaux outils avant vos concurrents",
            "Vous devenez la personne \"qui s'y connaît en IA\" dans votre entourage pro",
          ],
        },
        {
          track: "AI Mastery Enterprise",
          profile: "L'entreprise IA-ready",
          paused: true,
          description: "L'IA n'est plus un sujet de curiosité. C'est un levier de performance intégré.",
          outcomes: [
            "Vos équipes utilisent l'IA de manière coordonnée et efficace",
            "Vos process clés sont augmentés, documentés et reproductibles",
            "Vous avez une longueur d'avance concrète sur vos concurrents",
            "Chaque nouveau collaborateur est onboardé avec les bons réflexes IA",
          ],
        },
        {
          track: "AI Mastery Build",
          profile: "Le créateur de produits IA",
          description: "Vous n'avez plus juste une idée. Vous avez un produit.",
          outcomes: [
            "Vous avez un prototype fonctionnel entre les mains",
            "Vous maîtrisez les outils pour itérer en autonomie",
            "Vous pouvez pitcher votre produit avec une démo concrète",
            "Vous savez ce qui est faisable avec l'IA, et ce qui ne l'est pas encore",
          ],
        },
      ],
    },
    cta: {
      sectionLabel: "Commencez maintenant",
      headingPre: "Prêt à mettre l'IA ",
      headingAccent: "au travail",
      headingPost: " ?",
      subtitle: "30 minutes pour évaluer ensemble si l'accompagnement correspond à votre situation. Pas de pitch commercial — un échange direct pour voir si on est le bon fit.",
      primaryCta: "Réserver ma session découverte",
      secondaryCta: "Voir nos services",
      note: "30 min · Gratuit · Entretien de présélection",
    },
    leadMagnet: {
      bannerText: "Guide gratuit — Par où commencer avec l'IA sans vous éparpiller.",
      bannerCta: "Télécharger",
      sectionLabel: "Ressource offerte",
      badge: "Guide gratuit · PDF",
      headingPre: "Vous ne savez pas ",
      headingAccent: "par où commencer",
      headingPost: " avec l'IA ?",
      subtitle: "Recevez le guide de démarrage 100xWorks : un chemin clair pour passer de « je teste ChatGPT » à « l'IA travaille pour moi ». 9 pages, 5 minutes, zéro bullshit.",
      bullets: [
        "Les 4 niveaux d'utilisation de l'IA (et où vous vous situez vraiment)",
        "Pourquoi cartographier votre existant avant de toucher au moindre outil",
        "La méthode C.A.D.R.E. pour transformer une tâche en système IA",
        "Votre plan d'action sur 7 jours, 30 min par jour",
        "Les 5 erreurs de débutant qui vous font perdre 6 mois",
      ],
      namePlaceholder: "Votre prénom",
      emailPlaceholder: "Votre adresse email",
      cta: "Recevoir le guide gratuit",
      submitting: "Envoi…",
      note: "Vos données restent privées. Aucun email sans votre accord.",
      consentText: "J'accepte de recevoir par email des conseils IA et les actualités de 100xWorks. Désinscription possible à tout moment.",
      successTitle: "Votre guide est prêt 🎉",
      successBody: "Il vient de s'ouvrir dans un nouvel onglet. Vous pouvez le lire directement en ligne, ou cliquer sur « Enregistrer en PDF » en haut de la page pour le télécharger.",
      successCta: "Voir le guide",
      docUrl: "/lead-magnet/par-ou-commencer-avec-lia.html",
      errorMessage: "Une erreur est survenue. Réessayez dans un instant.",
      popupEyebrow: "Avant de partir…",
      popupTitle: "Repartez au moins avec le guide.",
      popupSubtitle: "Le guide de démarrage 100xWorks : par où commencer avec l'IA, sans vous éparpiller. Gratuit, 5 minutes.",
      popupDismiss: "Non merci, je ne veux pas démarrer avec l'IA",
    },
    cookies: {
      title: "On respecte votre vie privée",
      message: "On utilise des cookies de mesure d'audience (PostHog) pour comprendre comment le site est utilisé et l'améliorer. Rien n'est activé sans votre accord.",
      accept: "Tout accepter",
      decline: "Refuser",
      manage: "Gérer les cookies",
      privacy: "En savoir plus",
      privacyHref: "/confidentialite",
    },
    footer: {
      headerCta: "30 min offertes",
      description: "L'intelligence artificielle au service de votre quotidien et de votre entreprise. Conseil, accompagnement et intégration IA sur mesure.",
      servicesLabel: "Services",
      companyLabel: "Entreprise",
      contactLabel: "Contact",
      contactText: "Une question ? Un projet ? Parlons-en.",
      contactCta: "30 min offertes",
      legal: "Mentions légales",
      privacy: "Politique de confidentialité",
      cgv: "CGV",
      copyright: "Tous droits réservés.",
      footerServices: [
        { label: "AI Mastery One", href: "#services" },
        { label: "AI Mastery Enterprise", href: "#services" },
        { label: "AI Mastery Build", href: "#services" },
      ],
      footerCompany: [
        { label: "Nos activités", href: "/notre-histoire" },
        { label: "Méthode", href: "#methode" },
        { label: "Témoignages", href: "#temoignages" },
        { label: "FAQ", href: "#faq" },
        { label: "Notre histoire", href: "/notre-histoire" },
        { label: "Contact", href: "#contact" },
        { label: "The Jarvis Company", href: "/the-jarvis-company" },
      ],
    },
    jarvisCompany: {
      badge: "Société mère",
      headingPre: "The Jarvis ",
      headingAccent: "Company",
      headingPost: "",
      subtitle: "La structure derrière 100xWorks. Un fondateur, deux activités, une seule obsession : rendre l'IA utile au quotidien.",
      activitiesLabel: "Nos activités",
      activities: [
        {
          name: "100xWorks",
          tagline: "Accompagnement IA sur mesure",
          description: "Coaching individuel et collectif pour entrepreneurs, indépendants et PME. De la prise en main de l'IA à la construction de systèmes autonomes.",
          cta: "Découvrir 100xWorks",
          href: "/",
          icon: "GraduationCap",
          highlighted: true,
        },
        {
          name: "Produits numériques",
          tagline: "Conçus avec l'IA, pour résoudre de vrais problèmes",
          description: "The Jarvis Company conçoit ses propres produits digitaux. Cette double casquette (builder + accompagnateur) nourrit une approche concrète et pragmatique.",
          cta: "Bientôt disponible",
          href: null,
          icon: "Cpu",
          highlighted: false,
        },
      ],
      founderLabel: "Le fondateur",
      founderName: "Patrick 'pZ'",
      founderRole: "Founder",
      founderBio: "Passionné d'IA et d'entrepreneuriat, Patrick (alias pZ) a créé The Jarvis Company avec une conviction : l'IA ne doit pas rester un sujet de conversation, mais devenir un levier concret. Il pilote la vision, conçoit les programmes et accompagne chaque client personnellement.",
      founderImage: "https://customer-assets.emergentagent.com/job_541eaea2-9547-41a3-97f7-7400e537e73b/artifacts/otzlsqh5_28140BCA-A077-4FB3-9487-83C62A84B938.PNG",
      visionLabel: "Notre vision",
      visionText: "Un futur où chaque professionnel dispose d'un assistant IA qui gère le répétitif, anticipe les besoins, et lui permet de se concentrer sur ce qui compte vraiment. Pas l'IA pour l'IA, mais l'IA au service d'un objectif clair.",
      domainNote: "thejarvis.company · 100xworks sera bientôt accessible via thejarvis.company/100xworks",
      ctaHeading: "Un projet ? Une question ?",
      ctaSubtitle: "Réservez un appel découverte gratuit de 30 minutes. Pas de pitch commercial, juste un échange.",
      ctaButton: "30 min offertes. Zéro engagement.",
    },
    ourStory: {
      sectionLabel: "Notre histoire",
      backLink: "Retour à l'accueil",
      headingPre: "Une conviction, une ",
      headingAccent: "mission",
      headingPost: "",
      subtitle: "Comment la découverte de l'IA a changé notre vision du travail, et pourquoi nous avons décidé d'en faire profiter les autres.",
      origin: {
        title: "Le point de départ",
        paragraphs: [
          "Je m'appelle Patrick, pZ pour les intimes. Avant l'IA, j'ai fait mes armes dans la tech et les startups, avec un vrai goût pour l'automatisation : faire en sorte que les choses tournent sans qu'on ait à y repenser sans cesse.",
          "En 2022, j'ai basculé à fond dans l'intelligence artificielle. Pas par curiosité polie : plus de 400 outils testés, des dizaines de systèmes construits, une veille quasi quotidienne depuis. C'est devenu une obsession saine.",
          "Ce que j'ai vu en chemin m'a marqué : des entreprises qui investissent dans l'IA sans jamais vraiment s'en servir. Des chatbots à l'abandon, des automatisations bricolées, des équipes qui retournent à leurs vieilles habitudes au bout d'un mois.",
          "100xWorks est né de cette frustration. Mon objectif n'est pas de vous vendre un outil de plus. C'est de m'asseoir avec vous, comprendre votre métier, et construire ensemble ce qui va réellement changer votre quotidien."
        ],
      },
      mission: {
        title: "Notre mission",
        paragraphs: [
          "Démocratiser l'IA, ce n'est pas la rendre accessible. C'est rendre ses bénéfices concrets, pour vous. Je ne vous vends pas un outil. Je vous accompagne dans un changement concret.",
          "Chaque personne que j'accompagne reçoit un programme sur mesure. Pas de template, pas de solution universelle. Je prends le temps de comprendre vos défis et vos ambitions, puis on travaille intensément, ensemble, pour un résultat concret."
        ],
      },
      values: [
        { title: "Qualité plutôt que quantité", description: "5 clients en simultané maximum. Chaque accompagnement reçoit toute mon attention — jamais divisée entre des dizaines de personnes." },
        { title: "Transparence totale", description: "Je vous dis toujours ce que l'IA peut faire, et surtout ce qu'elle ne peut pas." },
        { title: "Impact mesurable", description: "Je conçois chaque intervention pour générer des résultats concrets et quantifiables." },
        { title: "Relation humaine", description: "100% à distance, mais jamais impersonnel. Votre succès, c'est ma réputation." },
      ],
      vision: {
        title: "Notre vision",
        paragraphs: [
          "L'IA ne remplace pas l'humain. Elle le libère. Chaque professionnel peut avoir un assistant intelligent qui gère le répétitif, anticipe les besoins, et lui permet de se concentrer sur ce qui compte vraiment.",
          "C'est cette vision qui guide chaque accompagnement que je mène. Pas l'IA pour l'IA. L'IA au service d'un objectif clair — le vôtre."
        ],
        quote: "L'IA la plus puissante est celle qu'on ne remarque pas, parce qu'elle s'intègre naturellement dans votre façon de travailler.",
        quoteAuthor: "Patrick 'pZ', Fondateur 100xWorks",
      },
      manifesto: {
        title: "Mes engagements",
        subtitle: "Pas de bullshit, des règles claires que je m'impose à moi-même.",
        signedBy: "— Patrick 'pZ', Fondateur",
        commitments: [
          { title: "Je ne travaille jamais avec plus de 5 clients en simultané.", description: "Pas d'enchaînement à la chaîne. Chaque client reçoit une attention totale, du premier échange au système livré." },
          { title: "Je ne touche aucune commission sur les outils que je recommande.", description: "Aucune affiliation. Je recommande ce qui marche pour vous, pas ce qui me rapporte." },
          { title: "Je vous dis quand l'IA ne sert à rien.", description: "Si votre vrai besoin n'est pas IA, je le dis. Mon job, c'est de vous faire gagner du temps, pas de vous vendre une techno." },
        ],
      },
      ctaHeading: "Envie d'écrire la suite ensemble ?",
      ctaSubtitle: "Chaque histoire est unique. Racontez-nous la vôtre lors d'un appel découverte gratuit.",
      ctaButton: "30 min offertes. Zéro engagement.",
    },
  },

  en: {
    navigation: [
      { label: "Services", href: "#services" },
      { label: "Results", href: "#resultats" },
      { label: "FAQ", href: "#faq" },
      { label: "Our Story", href: "/notre-histoire" },
    ],
    hero: {
      badge: "AI Coaching · Understand. Build. Deploy.",
      headingPre: "While you read this, AI is working for ",
      headingAccent: "someone else",
      headingPost: ".",
      subtitle: "1:1 coaching for executives, freelancers and employees. In 4 to 6 weeks, we build together the AI systems you'll use every day — and you stay autonomous after.",
      cta: "Book my discovery session",
      secondaryCta: "See if you qualify",
      ctaNote: "30 min · Free · No commitment",
      stats: [
        { value: "5 max", label: "Clients at a time" },
        { value: "100%", label: "Remote only" },
        { value: "1:1", label: "Direct relationship" },
      ],
      availability: "5 clients at a time",
      availabilityCta: "Now reviewing applications",
      scrollLabel: "Discover",
    },
    marquee: {
      tools: ["ChatGPT", "Claude", "Gemini", "Perplexity", "Hugging Face", "Hermes", "n8n", "ElevenLabs", "Cursor", "Higgsfield", "OpenClaw", "Stitch", "Genspark", "Paperclip", "Runway", "Notion", "Windsurf", "Emergent"],
    },
    manifesto: {
      pre: "GenAI is the ",
      strike: "future",
      replace: "present",
      post: " of work",
      caption: "",
    },
    reality: {
      narrativePre: "Everyone has access to the same tools.",
      narrativeQuote: "Not everyone knows how to make them work.",
      narrativePost: "Access isn't the advantage anymore. Implementation is.",
      callouts: [
        "You follow every AI launch.",
        "You bookmark every thread.",
        "You watch every demo.",
      ],
      punchline: "You follow every launch. Every thread. Every demo.",
      punchlineAccent: "Nothing changed.",
      sectionLabel: "The reality",
      statement: "Generative AI is not the future.",
      statementAccent: "It's the present.",
      statementSub: "CEOs, leaders and entrepreneurs who understood this are already transforming the way they work.",
      useCases: [
        { label: "Voice assistants", icon: "Mic" },
        { label: "Automation", icon: "Workflow" },
        { label: "AI assistants", icon: "Bot" },
        { label: "Internal tools", icon: "Monitor" },
        { label: "Content creation", icon: "PenTool" },
      ],
    },
    quotes: {
      items: [
        { text: "Software is eating the world, but AI is going to eat software.", author: "Jensen Huang", role: "CEO, NVIDIA" },
        { text: "AI will be the greatest force for economic empowerment we have ever seen.", author: "Sam Altman", role: "CEO, OpenAI" },
        { text: "AI will not just be a new tool, it will be a fundamental shift in how all technology is built and used.", author: "Marc Andreessen", role: "Andreessen Horowitz" },
        { text: "In the future, every company will be an AI company.", author: "Satya Nadella", role: "CEO, Microsoft" },
        { text: "AI is the symbiote. Makes you stronger, better, faster.", author: "Varun Mayya", role: "Founder & Entrepreneur" },
        { text: "AI could be the most transformative technology in the history of humanity.", author: "Dario Amodei", role: "CEO, Anthropic" },
      ],
    },
    problem: {
      sectionLabel: "The real problem",
      headingPre: "You're using AI. Your competitors are ",
      headingAccent: "deploying",
      headingPost: " it.",
      subtitle: "There's a difference between chatting with ChatGPT and having AI systems that handle your work. Most people never cross that line.",
      useCases: [
        { persona: "CEO / Executive", quote: "My team spends 3 hours a day on tasks AI could handle in 10 minutes. And I don't even know where to start to change that.", icon: "Briefcase" },
        { persona: "Freelancer", quote: "I use ChatGPT every day but I feel like I'm using 5% of its potential. I just copy-paste prompts from influencers.", icon: "User" },
        { persona: "Product creator", quote: "I have a specific app idea but no technical skills. Every quote I get is above 15K€. I want to learn to build it myself with AI.", icon: "Rocket" },
        { persona: "Employee / Manager", quote: "My company has been talking about AI for 2 years but nobody really uses it. I want to get ahead before it becomes mandatory.", icon: "TrendingUp" },
      ],
      codeNoCode: {
        line1: "Know how to code?",
        line1Accent: " Great.",
        line2: "Don't know how to code?",
        line2Accent: " No worries.",
        description: "Our approach blends code and no-code. We don't train you on a tool, we teach you to think like a solution architect and execute concretely, whatever your technical level.",
      },
    },
    proof: {
      beforeAfter: {
        heading: "Before 100xWorks vs After 100xWorks",
        before: {
          label: "Without 100xWorks",
          gridLabel: "Your automated tasks",
          gridCaption: "3 automated tasks / month",
          metrics: [
            { title: "15h/week on repetitive tasks", sub: "Copy-paste, sorting, manual writing..." },
            { title: "0 active AI systems", sub: "Occasional ChatGPT use" },
            { title: "Tools tried, then abandoned", sub: "Every task starts from scratch" },
          ],
        },
        after: {
          label: "With 100xWorks",
          gridLabel: "Your active AI systems",
          gridCaption: "17+ active systems / month",
          metrics: [
            { title: "3h saved / day", sub: "Your AI systems work while you decide" },
            { title: "5+ custom AI systems", sub: "Writing, monitoring, support, analysis..." },
            { title: "Autonomous workflows", sub: "Everything runs, even while you sleep" },
          ],
        },
        bottomLine: {
          pre: "Reclaim ",
          accent: "15h/week",
          post: " of your time",
        },
      },
      features: {
        heading: "What you get",
        subtitle: "Concrete systems, built with you, ready to run.",
        items: [
          { title: "Custom AI workflows", description: "Automated chains tailored to your business. From input to action, without intervention." },
          { title: "Custom AI assistants", description: "Your own AI assistants, trained on your context. They work for you, not the other way around." },
          { title: "Templates & Prompts", description: "A library of optimized, reusable prompts for every situation." },
          { title: "Automated planning", description: "Everything planned, organized, ready to go. You validate, AI executes." },
        ],
      },
    },
    caseStudies: {
      sectionLabel: "Case studies",
      headingPre: "What our clients ",
      headingAccent: "achieve",
      headingPost: "",
      subtitle: "Real results, achieved by real professionals during their coaching.",
      beforeLabel: "Before",
      afterLabel: "After coaching",
      cases: [
        {
          persona: "Independent consultant",
          track: "AI Mastery One",
          metric: "3h saved per day",
          before: "Spends 4h/day writing emails, proposals, and client reports. Every document written from scratch.",
          after: "Learned to build a system that generates personalized drafts of proposals and emails from smart templates. Writing time reduced to 45 min/day.",
        },
        {
          persona: "Consulting SME (8 people)",
          track: "AI Mastery Enterprise",
          metric: "Whole team autonomous on AI",
          before: "The team had been talking about AI for a year but no one really used it. Each colleague on their own, with no shared process.",
          after: "Upskilled collectively: each member co-built their own AI workflows during the program. A shared language, shared processes, real autonomy.",
        },
        {
          persona: "Career-switching entrepreneur",
          track: "AI Mastery Build",
          metric: "MVP launched for under 500 euros",
          before: "Has an app idea but no technical skills. Quotes received: 15,000 to 40,000 euros. Project stuck for 6 months.",
          after: "Learned to use no-code and AI tools to design, build, and launch an MVP in 3 weeks, without writing a single line of code.",
        },
      ],
    },
    savings: {
      sectionLabel: "The real ROI of AI",
      headingPre: "AI can handle ",
      headingAccent: "these roles",
      headingPost: " in your business",
      subtitle: "After our coaching, here are the functions your AI systems can manage, partially or fully.",
      totalLabel: "Total saved:",
      totalSuffix: "/month",
      note: "+ necessary AI tools and subscriptions",
      savingsPrefix: "Save",
      savingsUnit: "€/month",
      roles: [
        { name: "Administrative assistant", saving: 2000 },
        { name: "Community Manager", saving: 2500 },
        { name: "Content Writer", saving: 2000 },
        { name: "Customer Support (L1)", saving: 1800 },
        { name: "Data Analyst", saving: 2500 },
        { name: "Email Marketing", saving: 1500 },
        { name: "Competitive Intelligence", saving: 1200 },
        { name: "SEO Writer", saving: 1800 },
        { name: "Translator", saving: 2000 },
        { name: "Sales Prospecting", saving: 2200 },
        { name: "Recurring Accounting", saving: 1500 },
        { name: "Social Media Manager", saving: 2500 },
      ],
    },
    readyToBuild: {
      sectionLabel: "Ready to take action?",
      noiseTitle: "The noise won't stop.",
      noiseLines: [
        "You've tried pre-made prompts.",
        "YouTube courses. Twitter threads.",
        "'10 AI tools of the week' newsletters.",
        "And yet, nothing really changed in your daily work.",
      ],
      whyTitle: "Why one-size-fits-all doesn't work",
      whyText: "Because they don't know your job, your constraints, your goals. They're generic by design. What you need has to be custom-built.",
      howTitle: "Our coaching in 4 phases",
      phases: [
        { number: "01", title: "Diagnostic", description: "We analyze your current AI usage, daily tasks, and friction points.", duration: "Week 1" },
        { number: "02", title: "Foundations", description: "You learn the essential principles: advanced prompting, workflows, AI tools. Adapted to your level.", duration: "Weeks 2-3" },
        { number: "03", title: "Building", description: "You build your own systems: workflows, tools and custom AI solutions for your business.", duration: "Weeks 3-4" },
        { number: "04", title: "Autonomy", description: "You're autonomous. We validate, adjust, and you leave with skills that last.", duration: "Ongoing" },
      ],
      cta: "Book my discovery session",
      counter: "Only 3 spots remaining",
      metrics: [
        { value: "3h", label: "saved per day on average" },
        { value: "x5", label: "content productivity" },
        { value: "<500€", label: "to launch an MVP" },
        { value: "100%", label: "of clients autonomous at the end" },
      ],
    },
    about: {
      sectionLabel: "Our Work",
      headingPre: "One founder, one ",
      headingAccent: "mission",
      headingPost: ".",
      description: "100xWorks guides entrepreneurs, freelancers, and SMEs in practically integrating AI into their daily work. From diagnosis to autonomy, we help you build AI systems that work for you.",
      secondParagraph: "A single founder, monitoring AI daily, testing the latest developments and translating them into actionable solutions. Direct relationship from first contact to final result. Number of clients deliberately limited.",
      stats: [
        { value: "5 max", label: "At a time" },
        { value: "100%", label: "Remote" },
      ],
      learnMore: "Learn more",
      founderQuote: "A solofounder: your single point of contact, from start to finish",
    },
    team: {
      sectionLabel: "The team",
      headingPre: "The faces behind ",
      headingAccent: "100xWorks",
      headingPost: "",
      subtitle: "One founder. Three AI agents. A hybrid team that practices what it teaches.",
      members: [
        {
          name: "Patrick 'pZ'",
          title: "Founder",
          badge: "Human",
          bio: "Passionate about AI and entrepreneurship, Patrick (a.k.a. pZ) designs programs, coaches clients, and drives the company's vision.",
          image: "https://customer-assets.emergentagent.com/job_541eaea2-9547-41a3-97f7-7400e537e73b/artifacts/otzlsqh5_28140BCA-A077-4FB3-9487-83C62A84B938.PNG",
        },
        {
          name: "Hector",
          title: "Chef AI Officer",
          badge: "AI Agent",
          bio: "My right hand. Hector coordinates operations, analyzes data, and optimizes every process daily.",
          image: "https://static.prod-images.emergentagent.com/jobs/b7ea81d9-46ff-4176-9d90-88843ace2527/images/88ab2d4f62f0328d5a89909870f178b500468c37e41588782e4669f1ae4c0fb9.png",
        },
        {
          name: "Gérard",
          title: "Program Builder",
          badge: "AI Agent",
          bio: "A teacher at heart, Gérard helps me build and structure every coaching program, with rigor and passion.",
          image: "https://static.prod-images.emergentagent.com/jobs/b7ea81d9-46ff-4176-9d90-88843ace2527/images/51028b1c7c53f9792635966621f6a93563fb899486eef62d4bc0d3aeb94829fb.png",
        },
        {
          name: "Laure",
          title: "Support",
          badge: "AI Agent",
          bio: "Laure helps me provide responsive, personalized support for every client, at every step of the journey.",
          image: "https://static.prod-images.emergentagent.com/jobs/b7ea81d9-46ff-4176-9d90-88843ace2527/images/de0093c5a2409728c24ddeb008c5e2c7725ac0952ae09b715122e791f02e199d.png",
        },
      ],
    },
    amplify: {
      sectionLabel: "Our philosophy",
      headingPre: "AI doesn't replace ",
      headingAccent: "humans",
      headingPost: "",
      subtitle: "It replaces tasks. And it amplifies those who know how to use it.",
      without: {
        title: "Human alone",
        subtitle: "Capacity limited by time",
        points: [
          "Everything done manually, task by task",
          "60% of time on repetitive work",
          "Thinks AND executes in parallel",
          "Hits a ceiling despite the skills",
        ],
      },
      with: {
        title: "Human + AI",
        subtitle: "Capacity multiplied by 10",
        points: [
          "Delegates the repetitive to AI",
          "Focuses on strategy and decisions",
          "Thinks differently, executes differently",
          "Becomes the brain, AI the hands",
        ],
      },
      pyramid: {
        intro: "What changes with 100xWorks:",
        layers: [
          { label: "Vision & Strategy", desc: "What you do, and what AI can't replace" },
          { label: "Augmented execution", desc: "What AI amplifies: you decide, it executes" },
          { label: "Repetitive tasks", desc: "What AI replaces: emails, sorting, writing, monitoring..." },
        ],
        labelTop: "You",
        labelBottom: "AI",
      },
      punchline: {
        pre: "We don't teach you how to use a tool. We teach you to ",
        accent: "think and execute differently",
        post: ".",
      },
    },
    services: {
      sectionLabel: "Services",
      headingPre: "Two tracks, one ",
      headingAccent: "goal",
      headingPost: ": your autonomy",
      contact: "Get in touch",
      learnMore: "Learn more",
      items: [
        {
          title: "AI Mastery Build",
          description: "For those who want to build their product with generative AI. From idea to working prototype: app, interface, internal tool. You build, we guide. Intensive project coaching.",
          icon: "Cpu",
        },
        {
          title: "AI Mastery One",
          description: "For those who want to master generative AI in their daily work. From getting started with tools to building your first AI systems. 1:1 sessions, adapted to your level.",
          icon: "GraduationCap",
        },
        {
          title: "AI Mastery Enterprise",
          description: "Group coaching for teams that want to upskill on AI. We don't deploy for you, we co-build with your team. Your people learn to design and execute their own AI workflows, and stay autonomous afterwards.",
          icon: "Search",
        },
      ],
    },
    fit: {
      sectionLabel: "Let's be clear",
      headingPre: "This coaching is ",
      headingAccent: "not for everyone",
      headingPost: ".",
    },
    forYou: {
      sectionLabel: "Do you recognize yourself?",
      headingPre: "This coaching is ",
      headingAccent: "made for you",
      headingPost: " if...",
      items: [
        "You actually want to master AI, not just follow YouTube tutorials.",
        "You're ready to practice between sessions, not just listen.",
        "You're looking for tailored coaching adapted to your work and level.",
        "You want a direct relationship with a single point of contact, not an impersonal platform.",
        "You want visible results within the first weeks, not in six months.",
      ],
    },
    pricing: {
      sectionLabel: "Pricing",
      headingPre: "Transparent ",
      headingAccent: "investment",
      headingPost: "",
      subtitle: "Each track is tailored to your needs. Applications are reviewed — the discovery session determines whether we're the right fit.",
      cta: "Book my discovery session",
      ctaNote: "30 min · Free · Qualification interview",
      pausedCta: "Coming back soon",
      note: "The next 5 spots are available at these rates. Price will increase after.",
      tiers: [
        {
          name: "AI Mastery Build",
          tagline: "Build your product",
          price: "2,500",
          priceRaw: 2500,
          priceUnit: "€",
          priceSuffix: "excl. tax",
          pricePrefix: "",
          duration: "6 weeks",
          features: [
            "Project scoping & feasibility",
            "AI tools & technology selection",
            "Intensive coaching sessions",
            "From idea to working prototype",
            "Continuous iterations & adjustments",
            "Priority access to the founder",
          ],
          highlighted: false,
          badge: "Founder Mode",
        },
        {
          name: "AI Mastery One",
          tagline: "Master AI in your daily work",
          price: "1,500",
          priceRaw: 1500,
          priceUnit: "€",
          priceSuffix: "excl. tax",
          pricePrefix: "",
          duration: "4 to 6 weeks",
          features: [
            "Audit of your current AI usage",
            "Individual sessions (1:1)",
            "Prompting, workflows, AI tools",
            "Support between sessions",
            "Personalized resources",
          ],
          highlighted: true,
          badge: "Recommended",
        },
        {
          name: "AI Mastery Enterprise",
          tagline: "Integrate AI into your processes",
          price: "Custom quote",
          priceRaw: null,
          priceUnit: "",
          priceSuffix: "",
          pricePrefix: "",
          duration: "Custom",
          paused: true,
          badge: "On hold",
          features: [
            "Audit of existing workflows",
            "Custom collective action plan",
            "Group sessions + targeted 1:1",
            "Co-build AI workflows with your teams",
            "Follow-up & adjustments",
            "Single point of contact: the founder",
            "Your teams autonomous at the end",
          ],
          highlighted: false,
        },
      ],
    },
    howItWorks: {
      sectionLabel: "How it works",
      headingPre: "Coaching, ",
      headingAccent: "not a training course",
      headingPost: "",
      subtitle: "No standardized curriculum, no generic slides: we get to know each other, then work intensively on your real cases for a concrete result in 4 to 6 weeks.",
      steps: [
        {
          number: "01",
          title: "Diagnostic",
          description: "The free 30-minute discovery call was only a pre-diagnostic. Here, we go much deeper: your work, your real AI level, your precise goals. I understand your context in depth before building the plan.",
          detail: "In-depth diagnostic · Goals · Action plan",
        },
        {
          number: "02",
          title: "Skill building",
          description: "We build the foundations together live: prompting, AI reasoning, tools tailored to your work. Between sessions, you practice on targeted exercises. Every concept is grounded in your reality — no theory in the abstract.",
          detail: "Live sessions · Targeted exercises · 4 to 6 weeks",
        },
        {
          number: "03",
          title: "Build",
          description: "We shift to your real situation: your tasks, your documents, your constraints. We build your AI systems, remove friction points, and iterate until it runs. Direct support between sessions so you never get stuck.",
          detail: "Real case · AI systems · Ongoing support",
        },
      ],
    },
    notForYou: {
      sectionLabel: "Let's be clear",
      headingPre: "This coaching is ",
      headingAccent: "not for you",
      headingPost: " if...",
      items: [
        "You're looking for a shortcut to skip learning and just get a turnkey result.",
        "You want us to do it for you instead of building your own skills.",
        "You're not ready to invest time (a few hours per week, consistently).",
        "You expect the magic prompt that solves everything in a single copy-paste.",
        "You're looking for a generic online course to consume on your own with no interaction.",
      ],
      closing: "If you're still here, apply. The discovery session is a qualification interview — acceptance is not automatic.",
    },
    testimonials: {
      sectionLabel: "Testimonials",
      headingPre: "What those who took ",
      headingAccent: "the leap",
      headingPost: " say",
      items: [
        {
          name: "Marie D.",
          role: "Freelance digital marketer",
          quote: "I thought I was using ChatGPT well. In reality, I was barely scratching the surface. After the coaching, I have AI systems managing my monitoring, emails, and content creation. A total game changer.",
          metric: { value: "3x", label: "Productivity" },
          initials: "MD",
        },
        {
          name: "Thomas L.",
          role: "CEO, SaaS startup (12 people)",
          quote: "The approach is sincere and no-nonsense. No grand speeches about the future of AI, just concrete solutions that work today. Working directly with the founder makes all the difference.",
          metric: { value: "-50%", label: "Manual tasks" },
          initials: "TL",
        },
        {
          name: "Sophie B.",
          role: "SME owner, HR consulting",
          quote: "I was afraid of AI, didn't know where to start. The coaching helped me demystify everything and build simple but incredibly effective systems.",
          metric: { value: "10h", label: "Saved per week" },
          initials: "SB",
        },
        {
          name: "Pierre M.",
          role: "Independent financial consultant",
          quote: "What convinced me was the honesty. No promises of revolution, just pragmatic guidance that took me from 'I ask ChatGPT questions' to 'AI works for me.'",
          metric: { value: "100%", label: "Remote" },
          initials: "PM",
        },
      ],
    },
    faq: {
      headingPre: "Frequently ",
      headingAccent: "asked",
      headingPost: " questions",
      subtitle: "Everything you need to know before getting started with AI.",
      items: [
        {
          question: "What exactly is generative AI?",
          answer: "Generative AI is a technology capable of creating content (text, images, code, audio...) and executing complex tasks for you. Where a search engine gives you links, generative AI writes your emails, analyzes your data, creates your visuals, and automates your processes. It's the difference between searching for information and making it work for you.",
        },
        {
          question: "Is this a training course?",
          answer: "No, and that's deliberate. 100xWorks is not a training organization: no standardized curriculum, no generic slides, no certificate. It's coaching: we work on your real cases, with your tools, until you're autonomous. A course delivers content. Coaching gets you across the line.",
        },
        {
          question: "How does the coaching work in practice?",
          answer: "It starts with a 30-minute qualification interview. This is not a sales call: we assess together whether the coaching is the right fit for your situation. If it is, we begin with an in-depth diagnostic that sets the action plan. Then: skill building on your real cases, building your AI systems, ongoing support. The goal: for you to be autonomous by the end.",
        },
        {
          question: "Who delivers the coaching?",
          answer: "Me, the founder. 100xWorks is a human-scale structure. No subcontractors, no team that changes mid-way. You work directly with the same person from start to finish. That's also why the number of clients is limited.",
        },
        {
          question: "Why so few clients at a time?",
          answer: "It's a deliberate choice. Working with more than 5 clients simultaneously means diluting attention. Each engagement takes time, focus, and energy. The number stays limited so every client gets a full commitment — not a fraction of one.",
        },
        {
          question: "Do I need technical knowledge?",
          answer: "Not at all. My approach is designed to be accessible to everyone, from complete beginners to more technical profiles. I adapt the vocabulary, examples, and pace to your level. The goal is to make you autonomous, not to impress you with jargon.",
        },
        {
          question: "Which AI tools do you use?",
          answer: "ChatGPT, Claude, Gemini, Perplexity, Hermes, OpenCode, n8n, and others as needed. I'm not affiliated with any tool. I recommend what makes sense for your situation. The goal isn't to sell you a stack, but to find what works for you.",
        },
        {
          question: "What is 100xWorks's other activity?",
          answer: "Alongside AI coaching, 100xWorks also designs its own digital products. This dual role (builder + coach) feeds the concrete and pragmatic approach to coaching, because we practice what we teach every day.",
        },
        {
          question: "Do you work with large corporations?",
          answer: "It's possible, but conditional. The coaching is sized for individuals, freelancers, and SMEs. For larger structures, the project needs to be compatible with my way of working (human scale, direct relationship, no hierarchical layers). We'll discuss it during the discovery call.",
        },
        {
          question: "Is everything done remotely?",
          answer: "Yes, 100%. All coaching is done via video call. It's a deliberate choice: more scheduling flexibility, no unnecessary travel, and an exchange quality that rivals in-person work.",
        },
        {
          question: "How quickly do you see results?",
          answer: "Often from the first sessions. The idea is to generate quick wins fast: simple systems that save you time concretely. To go further (advanced AI systems, complex workflows), expect 1 to 3 months depending on the project's ambition.",
        },
        {
          question: "Can we start with a one-time mission?",
          answer: "Absolutely. A quick audit, a one-day training, help with a specific workflow: I adapt. No obligation for long-term commitment. Many clients start with a short mission and come back to go further.",
        },
      ],
    },
    outcomes: {
      sectionLabel: "After the coaching",
      headingPre: "What you ",
      headingAccent: "become",
      headingPost: "",
      subtitle: "Each track leads to a concrete result. Here's what changes for you after the program.",
      tracks: [
        {
          track: "AI Mastery One",
          profile: "The augmented professional",
          description: "You no longer \"try out\" AI. You use it as a daily lever.",
          outcomes: [
            "You save several hours per week thanks to your AI systems",
            "You build your own workflows without depending on anyone",
            "You can evaluate and adopt new tools before your competitors",
            "You become the go-to \"AI person\" in your professional circle",
          ],
        },
        {
          track: "AI Mastery Enterprise",
          profile: "The AI-ready company",
          paused: true,
          description: "AI is no longer a curiosity. It's an integrated performance lever.",
          outcomes: [
            "Your teams use AI in a coordinated and efficient way",
            "Your key processes are augmented, documented and reproducible",
            "You have a concrete head start over your competitors",
            "Every new team member is onboarded with the right AI reflexes",
          ],
        },
        {
          track: "AI Mastery Build",
          profile: "The AI product creator",
          description: "You no longer just have an idea. You have a product.",
          outcomes: [
            "You have a working prototype in your hands",
            "You master the tools to iterate independently",
            "You can pitch your product with a concrete demo",
            "You know what's feasible with AI, and what's not yet",
          ],
        },
      ],
    },
    cta: {
      sectionLabel: "Get started",
      headingPre: "Ready to put AI ",
      headingAccent: "to work",
      headingPost: "?",
      subtitle: "30 minutes to assess together whether the coaching fits your situation. No sales pitch — a direct conversation to see if we're the right fit.",
      primaryCta: "Book my discovery session",
      secondaryCta: "View our services",
      note: "30 min · Free · Qualification interview",
    },
    leadMagnet: {
      bannerText: "Free guide — Where to start with AI without scattering yourself.",
      bannerCta: "Download",
      sectionLabel: "Free resource",
      badge: "Free guide · PDF",
      headingPre: "Don't know ",
      headingAccent: "where to start",
      headingPost: " with AI?",
      subtitle: "Get the 100xWorks starter guide: a clear path from \"I'm testing ChatGPT\" to \"AI works for me\". 9 pages, 5 minutes, zero bullshit.",
      bullets: [
        "The 4 levels of AI usage (and where you really stand)",
        "Why to map your existing work before touching any tool",
        "The S.C.A.L.E. method to turn a task into an AI system",
        "Your 7-day action plan, 30 min a day",
        "The 5 beginner mistakes that cost you 6 months",
      ],
      namePlaceholder: "Your first name",
      emailPlaceholder: "Your email address",
      cta: "Get the free guide",
      submitting: "Sending…",
      note: "Your data stays private. No email without your consent.",
      consentText: "I agree to receive AI tips and updates from 100xWorks by email. Unsubscribe anytime.",
      successTitle: "Your guide is ready 🎉",
      successBody: "It just opened in a new tab. You can read it online, or click \"Save as PDF\" at the top of the page to download it.",
      successCta: "View the guide",
      docUrl: "/lead-magnet/getting-started-with-ai.html",
      errorMessage: "Something went wrong. Please try again in a moment.",
      popupEyebrow: "Before you go…",
      popupTitle: "At least leave with the guide.",
      popupSubtitle: "The 100xWorks starter guide: where to start with AI, without scattering yourself. Free, 5 minutes.",
      popupDismiss: "No thanks, I don't want to get started with AI",
    },
    cookies: {
      title: "We respect your privacy",
      message: "We use analytics cookies (PostHog) to understand how the site is used and improve it. Nothing is enabled without your consent.",
      accept: "Accept all",
      decline: "Decline",
      manage: "Manage cookies",
      privacy: "Learn more",
      privacyHref: "/confidentialite",
    },
    footer: {
      headerCta: "Free 30-min call",
      description: "Artificial intelligence at the service of your daily life and your business. AI consulting, coaching, and custom integration.",
      servicesLabel: "Services",
      companyLabel: "Company",
      contactLabel: "Contact",
      contactText: "A question? A project? Let's talk.",
      contactCta: "Free 30-min call",
      legal: "Legal Notice",
      privacy: "Privacy Policy",
      cgv: "Terms of Sale",
      copyright: "All rights reserved.",
      footerServices: [
        { label: "AI Mastery One", href: "#services" },
        { label: "AI Mastery Enterprise", href: "#services" },
        { label: "AI Mastery Build", href: "#services" },
      ],
      footerCompany: [
        { label: "Our Work", href: "/notre-histoire" },
        { label: "Method", href: "#methode" },
        { label: "Testimonials", href: "#temoignages" },
        { label: "FAQ", href: "#faq" },
        { label: "Our Story", href: "/notre-histoire" },
        { label: "Contact", href: "#contact" },
        { label: "The Jarvis Company", href: "/the-jarvis-company" },
      ],
    },
    jarvisCompany: {
      badge: "Parent company",
      headingPre: "The Jarvis ",
      headingAccent: "Company",
      headingPost: "",
      subtitle: "The structure behind 100xWorks. One founder, two activities, a single obsession: making AI useful in everyday life.",
      activitiesLabel: "Our activities",
      activities: [
        {
          name: "100xWorks",
          tagline: "Custom AI coaching",
          description: "Individual and team coaching for entrepreneurs, freelancers, and SMEs. From getting started with AI to building autonomous systems.",
          cta: "Discover 100xWorks",
          href: "/",
          icon: "GraduationCap",
          highlighted: true,
        },
        {
          name: "Digital products",
          tagline: "Built with AI, to solve real problems",
          description: "The Jarvis Company builds its own digital products. This dual role (builder + coach) feeds a concrete and pragmatic approach.",
          cta: "Coming soon",
          href: null,
          icon: "Cpu",
          highlighted: false,
        },
      ],
      founderLabel: "The founder",
      founderName: "Patrick 'pZ'",
      founderRole: "Founder",
      founderBio: "Passionate about AI and entrepreneurship, Patrick (a.k.a. pZ) created The Jarvis Company with one conviction: AI shouldn't remain a conversation topic, but become a concrete lever. He drives the vision, designs the programs, and coaches each client personally.",
      founderImage: "https://customer-assets.emergentagent.com/job_541eaea2-9547-41a3-97f7-7400e537e73b/artifacts/otzlsqh5_28140BCA-A077-4FB3-9487-83C62A84B938.PNG",
      visionLabel: "Our vision",
      visionText: "A future where every professional has an AI assistant that handles the repetitive, anticipates needs, and lets them focus on what truly matters. Not AI for AI's sake, but AI in service of a clear objective.",
      domainNote: "thejarvis.company · 100xworks will soon be available at thejarvis.company/100xworks",
      ctaHeading: "A project? A question?",
      ctaSubtitle: "Book a free 30-minute discovery call. No sales pitch, just a conversation.",
      ctaButton: "Free 30-min call. No strings.",
    },
    ourStory: {
      sectionLabel: "Our Story",
      backLink: "Back to home",
      headingPre: "A conviction, a ",
      headingAccent: "mission",
      headingPost: "",
      subtitle: "How the discovery of AI changed our vision of work, and why we decided to share it with others.",
      origin: {
        title: "The starting point",
        paragraphs: [
          "My name's Patrick — pZ to people who know me. Before AI, I cut my teeth in tech and startups, with a real taste for automation: making things run without having to constantly think about them.",
          "In 2022, I went all-in on artificial intelligence. Not out of polite curiosity: 400+ tools tested, dozens of systems built, near-daily monitoring ever since. It became a healthy obsession.",
          "What I saw along the way stuck with me: companies investing in AI without ever really using it. Abandoned chatbots, patched-together automations, teams sliding back to old habits within a month.",
          "100xWorks was born from that frustration. My goal isn't to sell you one more tool. It's to sit down with you, understand your work, and build together what will actually change your day-to-day."
        ],
      },
      mission: {
        title: "Our mission",
        paragraphs: [
          "Democratizing AI isn't about making it accessible. It's about making its benefits real — for you. I don't sell you a tool. I support you through concrete change.",
          "Everyone I work with gets a tailored program. No templates, no one-size-fits-all. I take the time to understand your challenges and ambitions, then we work intensively together for a concrete result."
        ],
      },
      values: [
        { title: "Quality over quantity", description: "No more than 5 clients at a time. Every engagement gets my full attention — never split across dozens of people." },
        { title: "Total transparency", description: "I always tell you what AI can do, and especially what it can't." },
        { title: "Measurable impact", description: "I design every engagement to generate concrete, measurable results." },
        { title: "Human connection", description: "100% remote, but never impersonal. Your success is my reputation." },
      ],
      vision: {
        title: "Our vision",
        paragraphs: [
          "AI doesn't replace humans. It frees them. Every professional can have an intelligent assistant that handles the repetitive, anticipates needs, and lets them focus on what truly matters.",
          "That vision guides every engagement I run. Not AI for AI's sake. AI in service of a clear objective — yours."
        ],
        quote: "The most powerful AI is the one you don't notice, because it naturally integrates into the way you work.",
        quoteAuthor: "Patrick 'pZ', Founder 100xWorks",
      },
      manifesto: {
        title: "My commitments",
        subtitle: "No bullshit. Clear rules I hold myself to.",
        signedBy: "— Patrick 'pZ', Founder",
        commitments: [
          { title: "I never work with more than 5 clients at a time.", description: "No client treadmill. Every client gets full attention from day one to the moment their system is live." },
          { title: "I take zero commission on the tools I recommend.", description: "No affiliations. I recommend what works for you, not what pays me." },
          { title: "I tell you when AI isn't the answer.", description: "If your real need isn't AI, I'll say so. My job is to save you time, not sell you tech." },
        ],
      },
      ctaHeading: "Ready to write the next chapter together?",
      ctaSubtitle: "Every story is unique. Tell us yours during a free discovery call.",
      ctaButton: "Free 30-min call. No strings.",
    },
  },
};
